import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, DotsThreeVertical, PencilSimple, Trash, UsersThree, MagnifyingGlass, ShieldCheck, Key,
  User, EnvelopeSimple, Phone,
} from "@phosphor-icons/react";
import { ROLE_BADGE } from "@/lib/roles";
import { cn } from "@/lib/utils";

const STAFF_ROLES = ["super_admin", "director", "branch_manager", "secretary", "teacher", "accountant"];

const emptyForm = { name: "", email: "", phone: "", role: "secretary", password: "", active: true };

function StaffForm({ initial, onSaved, onClose }) {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [f, setF] = useState(initial ? {
    ...emptyForm, ...initial, password: "",
  } : emptyForm);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.name || !f.email || !f.role) return toast.error(t("common.required"));
    if (!isEdit && (!f.password || f.password.length < 6)) return toast.error("Password (min 6 chars) is required");
    setSaving(true);
    try {
      const payload = { name: f.name, email: f.email, phone: f.phone, role: f.role, active: f.active };
      if (f.password) payload.password = f.password;
      if (isEdit) {
        const { data } = await api.put(`/staff/${initial.id}`, payload);
        toast.success(t("common.saved"));
        onSaved(data);
      } else {
        const { data } = await api.post("/staff", payload);
        toast.success(t("common.saved"));
        onSaved(data);
      }
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("common.name")} *</Label>
          <Input data-testid="staff-form-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 h-10" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("common.email")} *</Label>
          <Input data-testid="staff-form-email" type="email" value={f.email} disabled={isEdit}
                 onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5 h-10" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("common.phone")}</Label>
          <Input data-testid="staff-form-phone" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5 h-10" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("common.role")} *</Label>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="staff-form-role" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STAFF_ROLES.map((r) => <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">
            {isEdit ? "New password (leave blank to keep)" : "Password *"}
          </Label>
          <Input data-testid="staff-form-password" type="password" value={f.password}
                 onChange={(e) => setF({ ...f, password: e.target.value })}
                 placeholder={isEdit ? "••••••••" : "min 6 characters"} className="mt-1.5 h-10" />
        </div>
        <div className="md:col-span-2 flex items-center gap-2">
          <input id="active" data-testid="staff-form-active" type="checkbox" checked={!!f.active}
                 onChange={(e) => setF({ ...f, active: e.target.checked })}
                 className="w-4 h-4 rounded border-border" />
          <Label htmlFor="active" className="text-xs font-semibold uppercase tracking-wider">{t("common.active")}</Label>
        </div>
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
        <Button data-testid="staff-form-save" onClick={submit} disabled={saving} className="gap-2">
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canManage = ["super_admin", "director"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter) params.role = roleFilter;
      const { data } = await api.get("/staff", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (canManage) load(); /* eslint-disable-next-line */ }, [roleFilter]);

  const remove = async (id, name) => {
    if (!window.confirm(`${t("common.delete")}: ${name}?`)) return;
    try { await api.delete(`/staff/${id}`); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const kpis = useMemo(() => {
    const byRole = STAFF_ROLES.map((r) => ({ role: r, count: rows.filter((u) => u.role === r).length }));
    return { total: rows.length, byRole };
  }, [rows]);

  if (!canManage) {
    return (
      <div className="space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.users")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("nav.users")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Only Super Admin or Director can manage staff.</p>
        </div>
        <Card className="border-border/60"><CardContent className="p-10 text-center text-sm text-muted-foreground">
          You do not have permission to view this page.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.users")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>Staff & Role Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Create, edit, and manage staff accounts and their roles.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-staff-btn" onClick={() => setEditing(null)} className="rounded-full gap-2">
              <Plus size={16} weight="bold" /> Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Manrope" }}>
                {editing ? "Edit Staff" : "New Staff Member"}
              </DialogTitle>
            </DialogHeader>
            <StaffForm initial={editing} onSaved={load} onClose={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="border-border/60"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <UsersThree size={16} weight="duotone" />
            <span className="text-[10px] uppercase tracking-widest font-semibold">{t("common.total")}</span>
          </div>
          <div className="text-2xl font-bold font-mono mt-1" data-testid="staff-total">{kpis.total}</div>
        </CardContent></Card>
        {kpis.byRole.map((r) => (
          <Card key={r.role} className="border-border/60"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{t(`roles.${r.role}`)}</div>
            <div className="text-2xl font-bold font-mono mt-1" data-testid={`staff-count-${r.role}`}>{r.count}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="staff-search" placeholder={t("common.search")} value={q}
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className="pl-10 h-10" />
        </div>
        <Select value={roleFilter || "__all"} onValueChange={(v) => setRoleFilter(v === "__all" ? "" : v)}>
          <SelectTrigger data-testid="staff-role-filter" className="h-10 w-56"><SelectValue placeholder={t("common.filter")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">{t("common.all")}</SelectItem>
            {STAFF_ROLES.map((r) => <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-border/60"><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="text-left px-4 py-3 font-semibold">{t("common.name")}</th>
                <th className="text-left px-4 py-3 font-semibold">{t("common.email")}</th>
                <th className="text-left px-4 py-3 font-semibold">{t("common.role")}</th>
                <th className="text-left px-4 py-3 font-semibold">{t("common.phone")}</th>
                <th className="text-left px-4 py-3 font-semibold">{t("common.status")}</th>
                <th className="text-right px-4 py-3 font-semibold w-16">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">{t("common.loadingDots")}</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">{t("common.noResults")}</td></tr>
              )}
              {rows.map((u) => {
                const badge = ROLE_BADGE[u.role];
                const isSelf = u.id === user.id;
                return (
                  <tr key={u.id} data-testid={`staff-row-${u.id}`}
                      className="border-b border-border hover:bg-secondary/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                          {u.avatar ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                            : <span className="text-xs font-semibold">{u.name?.charAt(0)}</span>}
                        </div>
                        <div>
                          <div className="font-semibold">{u.name}{isSelf && <span className="text-[10px] text-muted-foreground ml-2">(you)</span>}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge className={cn("text-[10px] font-semibold", badge?.color)}>{t(`roles.${u.role}`)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.phone || "—"}</td>
                    <td className="px-4 py-3">
                      {u.active === false
                        ? <Badge variant="outline" className="text-[10px]">{t("common.inactive")}</Badge>
                        : <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20">{t("common.active")}</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`staff-actions-${u.id}`}>
                            <DotsThreeVertical size={16} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem data-testid={`staff-edit-${u.id}`} onClick={() => { setEditing(u); setOpen(true); }}>
                            <PencilSimple size={14} className="mr-2" /> {t("common.edit")}
                          </DropdownMenuItem>
                          {!isSelf && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem data-testid={`staff-delete-${u.id}`} className="text-destructive" onClick={() => remove(u.id, u.name)}>
                                <Trash size={14} className="mr-2" /> {t("common.delete")}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}
